import { AppSettingsService } from 'src/app/core/services/app-settings.service';
import { FormattingService } from '../../services/formatting.service';
import { Component, OnInit, OnDestroy, HostListener, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { beforeDateValidator } from 'src/app/core/directives/before-date.directive';
import { debounceTime, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { ActivatedRoute } from '@angular/router';
import { NewPeriodSelectorInterval } from '../../enums/newPeriodSelectorInterval';
import { DayPeriodSelectorItem } from './Items/day-period-selector-item';
import { WeekPeriodSelectorItem } from './Items/week-period-selector-item';
import { FourWeekPeriodSelectorItem } from './Items/four-week-period-selector-item';
import { MonthPeriodSelectorItem } from './Items/month-period-selector-item';
import { QuarterPeriodSelectorItem } from './Items/quarter-period-selector-item';
import { CropToDatePeriodSelectorItem } from './Items/crop-to-date-period-selector-item';
import { CropWeekPeriodSelectorItem } from './Items/crop-week-period-selector-item';
import { ToDatePeriodSelectorItem } from './Items/to-date-period-selector-item';
import { FullPeriodSelectorItem } from './Items/full-period-selector-item';
import { YearPeriodSelectorItem } from './Items/year-period-selector-item';
import { NewPeriodSelectorItem } from './Items/base/new-period-selector-item';
import { NewPeriodSelectorService } from '../../services/new-period-selector.service';
import { NewPeriodSelectorConfiguration } from '../../models/period-selector/newPeriodSelectorConfiguration';
import { NewCalendarPeriodFilter } from '../../models/period-selector/newCalendarPeriodFilter';
import { NewCropCyclePeriodFilter } from '../../models/period-selector/newCropCyclePeriodFilter';
import { PeriodHelper } from '../../helpers/period.helper';
import { NewPeriodFilter } from '../../models/period-selector/newPeriodFilter';
import { CropCycleService } from 'src/app/core/services/crop-cycle.service';
import { Constants } from '../../constants';
import cloneDeep from 'lodash/cloneDeep';
import { DatePipe } from '@angular/common';


@Component({
    selector: 'app-new-period-selector',
    templateUrl: './new-period-selector.component.html',
    styleUrls: ['./new-period-selector.component.scss'],
    standalone: false
})
export class NewPeriodSelectorComponent implements OnInit, OnDestroy {
  @HostListener('document:wheel', ['$event']) onMousewheel(event) {
    this.popupVisible = false;
  }

  private initialize$ = new Subject<void>();
  periodSelectorItems: NewPeriodSelectorItem[];
  visiblePeriodSelectorItems: NewPeriodSelectorItem[];
  popupVisible = false;
  customPeriodSelectionForm: UntypedFormGroup;
  ngUnsubscribe = new Subject<boolean>();
  width = 365;
  isCustomPeriodSelected = false;
  hasCustomPeriod = false;
  lastPeriodSelectorIntervalKey = 'lastPeriodSelectorInterval';
  lastPeriodSelectorStartDateKey = 'lastPeriodSelectorStartDate';
  lastPeriodSelectorEndDayKey = 'lastPeriodSelectorEndDate';
  lastPeriodSelectorIntervalLenghtKey = 'lastPeriodSelectorIntervalLength';
  formattedDate: string;
  canGoForward = true;
  canGoBackward = true;
  userDateFormat: string;
  canNavigate: boolean;
  config: NewPeriodSelectorConfiguration;
  // todo: update this when adding this period selector to compare page
  cropCycleItemsMaxIntervalLength = 10;
  @Input() public showPeriodSelector: boolean;
  selectedPeriodFilter: NewPeriodFilter;
  configurationSubscription: Subscription;

  get startDateControl() {
    return this.customPeriodSelectionForm.get('startDate');
  }

  get startDateRequiredError() {
    return this.startDateControl.touched && this.startDateControl.hasError('required');
  }

  get endDateControl() {
    return this.customPeriodSelectionForm.get('endDate');
  }

  get endDateRequiredError() {
    return this.endDateControl.touched && this.endDateControl.hasError('required');
  }

  get startDateGreaterThanEndDateError() {
    return this.customPeriodSelectionForm.hasError('startDateGreaterThanEndDate');
  }

  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth < 1440) {
      this.width = 318;
    } else {
      this.width = 360;
    }
  }

  constructor(
    private readonly translateService: TranslateService,
    private readonly formattingService: FormattingService,
    private readonly fb: UntypedFormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private readonly newPeriodSelectorService: NewPeriodSelectorService,
    private readonly cropCycleService: CropCycleService,
    private readonly datePipe: DatePipe,
    private readonly appSettingsService: AppSettingsService
  ) {
    this.customPeriodSelectionForm = this.createCustomPeriodSelectionForm();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  ngOnInit(): void {
    this.initialize$.pipe(
      takeUntil(this.ngUnsubscribe),
      debounceTime(250)
      ).subscribe(() => {
          this.initializePeriodSelectorInternal();
      });

    // make sure the translations are loaded before using instant
    this.translateService.stream('Shell.periodSelector.week').pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.periodSelectorItems = [
        new DayPeriodSelectorItem(this.translateService.instant('Shell.periodSelector.day')),
        new WeekPeriodSelectorItem(this.translateService.instant('Shell.periodSelector.week')),
        new FourWeekPeriodSelectorItem(this.translateService.instant('Shell.periodSelector.fourWeek')),
        new MonthPeriodSelectorItem(this.translateService.instant('Shell.periodSelector.month')),
        new QuarterPeriodSelectorItem(this.translateService.instant('Shell.periodSelector.quarter')),
        new YearPeriodSelectorItem(this.translateService.instant('Shell.periodSelector.year')),
        new CropToDatePeriodSelectorItem(this.translateService.instant('Shell.cropCyclesPeriodSelector.cropToDate')),
        new CropWeekPeriodSelectorItem(this.translateService.instant('Shell.cropCyclesPeriodSelector.cropWeek')),
        new ToDatePeriodSelectorItem(this.translateService.instant('Shell.cropCyclesPeriodSelector.toDate')),
        new FullPeriodSelectorItem(this.translateService.instant('Shell.cropCyclesPeriodSelector.full')),
      ];
    });

    if (window.innerWidth < 1440) {
      this.width = 318;
    }

    this.formattingService.selectedDateFormatChanged$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(df => {
      this.userDateFormat = df;
    });
  }

  initializePeriodSelector() {
    this.initialize$.next();
  }

  initializePeriodSelectorInternal() {
    if (this.configurationSubscription) {
      this.configurationSubscription.unsubscribe();
    }
    this.configurationSubscription = this.newPeriodSelectorService.getPeriodSelectorConfiguration()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(config => {
        if (!config.visibleItems.length) {
          this.showPeriodSelector = false;
        } else {
          this.showPeriodSelector = true;
          const cropCycles = (this.activatedRoute.snapshot.queryParams['cropCycle']
            ?? this.activatedRoute.snapshot.queryParams['cropCycles'])?.split(',').map(cc => +cc);

          this.config = config;
          if (cropCycles) {
            this.cropCycleService.GetCropCyclesIntervals(cropCycles).pipe(takeUntil(this.ngUnsubscribe)).subscribe(ccis => {
              this.config.cropCycles = ccis;
              this.updateCropCycleItemsMaxIntervalLength(ccis);
              this.setVisibleItems();
              this.loadSelectedPeriod(config.selectedFilter);
            });
          } else {
            const cropCycles = config.cropCycles;
            this.updateCropCycleItemsMaxIntervalLength(cropCycles);
            this.setVisibleItems();
            this.loadSelectedPeriod(config.selectedFilter);
          }
        }
      });
  }

  private updateCropCycleItemsMaxIntervalLength(cropCycles) {
    if (cropCycles) {
        this.cropCycleItemsMaxIntervalLength  = Math.min(...cropCycles.map(cc =>
          this.isActiveCropCycle(cc)
            ? Math.ceil(moment().endOf('day').diff(moment(cc.startDate).startOf('day'), 'hours') / Constants.NUMBER_OF_HOURS_IN_A_WEEK) < 1
                ? 1
                : Math.ceil(moment().endOf('day').diff(moment(cc.startDate).startOf('day'), 'hours') / Constants.NUMBER_OF_HOURS_IN_A_WEEK)
            : Math.ceil(moment(cc.endDate).endOf('day').diff(moment(cc.startDate).startOf('day'), 'hours') / Constants.NUMBER_OF_HOURS_IN_A_WEEK)));
    }
  }

  private isActiveCropCycle(cc) {
    return moment(cc.startDate).endOf('day') < moment().endOf('day') && moment(cc.endDate).endOf('day') > moment().endOf('day');
  }

  setVisibleItems() {
    this.visiblePeriodSelectorItems = this.periodSelectorItems.filter(psi => psi.isVisible(this.config));
    this.hasCustomPeriod = this.config.visibleItems.find(psi => psi === NewPeriodSelectorInterval.Custom) !== undefined;
  }

  loadSelectedPeriod(selectedFilter?: NewPeriodFilter) {
    if (selectedFilter) {
      this.setSelectedItemFromProvidedFilter(selectedFilter);
    } else {
      let queryPeriodSelectorInterval = this.activatedRoute.snapshot.queryParams['periodSelectorInterval'];
      if (!queryPeriodSelectorInterval) {
        // todo: remove this when all the pages use the new period selector
        const queryPeriodSelectorType = this.activatedRoute.snapshot.queryParams['periodSelectorType'];
        if (queryPeriodSelectorType) {
          const isCalendarFilterUrl = this.activatedRoute.snapshot.queryParams['startDate'] !== undefined;
          queryPeriodSelectorInterval = PeriodHelper.getPeriodIntervalFromPeriodType(queryPeriodSelectorType, isCalendarFilterUrl);
        }
      }
      let selectedPeriodIntervalString = queryPeriodSelectorInterval;
      if (!selectedPeriodIntervalString) {
        selectedPeriodIntervalString = localStorage.getItem(this.lastPeriodSelectorIntervalKey);
      }
      if (!selectedPeriodIntervalString) {
        selectedPeriodIntervalString = this.config.defaultItem;
      }
      this.setSelectedItem(+selectedPeriodIntervalString, true);
    }
  }

  setSelectedItemFromProvidedFilter(selectedFilter: NewPeriodFilter) {
    this.updateVisualState(selectedFilter.periodInterval);
    this.setSelectedItemFilterFromProvidedFilter(selectedFilter);
  }

  setSelectedItemFilterFromProvidedFilter(selectedFilter: NewPeriodFilter) {
    const calendarFilter = PeriodHelper.asCalendarFilter(selectedFilter);
    if (calendarFilter) {
      selectedFilter.formattedPeriod = this.getFormattedCalendarPeriodFilter(calendarFilter);
    } else {
      const cropCycleFilter = PeriodHelper.asCropCycleFilter(selectedFilter);
      if (cropCycleFilter) {
        selectedFilter.formattedPeriod = this.getFormattedCropCyclePeriodFilter(cropCycleFilter);
      }
    }
    this.formattedDate = selectedFilter.formattedPeriod;
    this.setPeriodFilter(selectedFilter);
    this.checkIfGoingForwardIsPossible(selectedFilter);
    this.checkIfGoingBackwardIsPossible(selectedFilter);
  }

  setSelectedItem(selectedPeriodInterval: NewPeriodSelectorInterval, useUrl: boolean = false) {
    this.updateVisualState(selectedPeriodInterval);
    this.setSelectedItemFilter(useUrl);
  }

  updateVisualState(selectedPeriodInterval: NewPeriodSelectorInterval) {
    this.visiblePeriodSelectorItems.forEach(i => i.isActive = false);
    this.isCustomPeriodSelected = false;
    this.canNavigate = false;
    const selectedPeriodIntervalItem = this.visiblePeriodSelectorItems.find(psi => psi.interval === selectedPeriodInterval);
    if (this.isCalendarInterval(selectedPeriodInterval)) {
      if (selectedPeriodInterval !== NewPeriodSelectorInterval.Custom) {
        if (selectedPeriodIntervalItem) {
          selectedPeriodIntervalItem.isActive = true;
          this.canNavigate = selectedPeriodIntervalItem?.hasNavigation;
        }
      } else {
        this.isCustomPeriodSelected = true;
        this.canNavigate = true;
      }
    } else {
      if (selectedPeriodIntervalItem) {
        selectedPeriodIntervalItem.isActive = true;
        this.canNavigate = selectedPeriodIntervalItem?.hasNavigation;
      }
    }
    if (this.visiblePeriodSelectorItems.find(psi => psi.isActive) === undefined && !this.isCustomPeriodSelected) {
      this.visiblePeriodSelectorItems[0].isActive = true;
      this.canNavigate = this.visiblePeriodSelectorItems.find(psi => psi.isActive)?.hasNavigation;
    }
  }

  setSelectedItemFilter(useUrl: boolean) {
    let filter = useUrl
      ? this.getFilterFromUrl()
      : null;
    if (!filter) {
      const lastFilterInterval = +localStorage.getItem(this.lastPeriodSelectorIntervalKey);
      const lastFilterStartDate = localStorage.getItem(this.lastPeriodSelectorStartDateKey);
      const lastFilterEndDate = localStorage.getItem(this.lastPeriodSelectorEndDayKey);
      const lastFilterIntervalLength = +localStorage.getItem(this.lastPeriodSelectorIntervalLenghtKey);
      const selectedItem = this.visiblePeriodSelectorItems.find(psi => psi.isActive);
      if (selectedItem) {
        filter = selectedItem.getPeriodFilter();
      } else {
        if (lastFilterInterval === NewPeriodSelectorInterval.Custom) {
          const lastCalendarFilter = new NewCalendarPeriodFilter();
          lastCalendarFilter.startDate = moment(lastFilterStartDate).startOf('day').toDate();
          lastCalendarFilter.endDate = moment(lastFilterEndDate).endOf('day').toDate();
          lastCalendarFilter.periodInterval = lastFilterInterval;
          this.isCustomPeriodSelected = true;
          filter = lastCalendarFilter;
        }
      }
      const calendarFilter = PeriodHelper.asCalendarFilter(filter);
      if (calendarFilter) {
        if (useUrl && lastFilterInterval) {
          calendarFilter.startDate = moment(lastFilterStartDate).startOf('day').toDate();
          calendarFilter.endDate = moment(lastFilterEndDate).endOf('day').toDate();
        }
        filter.formattedPeriod = this.getFormattedCalendarPeriodFilter(calendarFilter);
      } else {
        const cropCycleFilter = PeriodHelper.asCropCycleFilter(filter);
        if (cropCycleFilter) {
          if (cropCycleFilter.periodInterval === NewPeriodSelectorInterval.ToDate) {
            PeriodHelper.asCropCycleFilter(filter).intervalLength = this.cropCycleItemsMaxIntervalLength;
          }
          if (useUrl && lastFilterInterval) {
            cropCycleFilter.intervalLength = lastFilterIntervalLength;
          }
          filter.formattedPeriod = this.getFormattedCropCyclePeriodFilter(cropCycleFilter);
        }
      }
    }
    this.formattedDate = filter.formattedPeriod;
    this.setPeriodFilter(filter);
    this.checkIfGoingForwardIsPossible(filter);
    this.checkIfGoingBackwardIsPossible(filter);
  }

  getFilterFromUrl(): NewPeriodFilter {
    const queryPeriodSelectorInterval = this.activatedRoute.snapshot.queryParams['periodSelectorInterval']
      ?? this.activatedRoute.snapshot.queryParams['periodSelectorType'];
    if (queryPeriodSelectorInterval &&
      (this.visiblePeriodSelectorItems.find(psi => psi.isActive)?.interval === +queryPeriodSelectorInterval ||
        (+queryPeriodSelectorInterval === NewPeriodSelectorInterval.Custom && this.isCustomPeriodSelected))) {
      if (this.isCalendarInterval(+queryPeriodSelectorInterval)) {
        const queryStartDate = this.activatedRoute.snapshot.queryParams['periodSelectorStartDate']
          ?? this.activatedRoute.snapshot.queryParams['startDate'];
        const queryEndDate = this.activatedRoute.snapshot.queryParams['periodSelectorEndDate']
          ?? this.activatedRoute.snapshot.queryParams['endDate'];
        if (queryStartDate && queryEndDate) {
          const calendarFilter = new NewCalendarPeriodFilter();
          calendarFilter.startDate = moment(queryStartDate).startOf('day').toDate();
          calendarFilter.endDate = moment(queryEndDate).endOf('day').toDate();
          calendarFilter.periodInterval = +queryPeriodSelectorInterval;
          calendarFilter.formattedPeriod = this.getFormattedCalendarPeriodFilter(calendarFilter);
          return calendarFilter;
        }
      } else {
        const queryIntervalLength = this.activatedRoute.snapshot.queryParams['periodSelectorIntervalLength']
          ?? this.activatedRoute.snapshot.queryParams['weekNumber'];;
        if (queryPeriodSelectorInterval) {
          const cropCycleFilter = new NewCropCyclePeriodFilter();
          cropCycleFilter.periodInterval = +queryPeriodSelectorInterval;
          cropCycleFilter.intervalLength = +queryIntervalLength;
          cropCycleFilter.formattedPeriod = this.getFormattedCropCyclePeriodFilter(cropCycleFilter);
          return cropCycleFilter;
        }
      }
    }
    return null;
  }

  isCalendarInterval(selectedPeriodInterval: NewPeriodSelectorInterval) {
    return !this.isCropCycleInterval(selectedPeriodInterval);
  }

  isCropCycleInterval(selectedPeriodInterval: NewPeriodSelectorInterval) {
    return selectedPeriodInterval === NewPeriodSelectorInterval.CropWeek ||
      selectedPeriodInterval === NewPeriodSelectorInterval.CropToDate ||
      selectedPeriodInterval === NewPeriodSelectorInterval.ToDate ||
      selectedPeriodInterval === NewPeriodSelectorInterval.Full;
  }

  createCustomPeriodSelectionForm() {
    return this.fb.group(
      {
        startDate: [undefined, Validators.required],
        endDate: [undefined, Validators.required]
      },
      { validators: beforeDateValidator }
    );
  }

  onCustomPeriodSelectorButtonClick() {
    this.visiblePeriodSelectorItems.forEach(i => i.isActive = false);
    this.isCustomPeriodSelected = false;
    this.popupVisible = true;
    this.isCustomPeriodSelected = true;

    const calendarFilter = PeriodHelper.asCalendarFilter(this.selectedPeriodFilter);
    if (calendarFilter) {
      this.startDateControl.setValue(calendarFilter.startDate);
      this.endDateControl.setValue(calendarFilter.endDate);
    } else {
      const cropCycleFilter = PeriodHelper.asCropCycleFilter(this.selectedPeriodFilter);
      if (cropCycleFilter) {
        this.setCropCycleFilterCustomDates(cropCycleFilter);
      }
    }
  }

  setCropCycleFilterCustomDates(cropCycleFilter) {
    const selectedItem = this.visiblePeriodSelectorItems.find(psi => psi.interval === cropCycleFilter.periodInterval);
    if (selectedItem) {
      let intervalStartDate;
      let intervalEndDate;
      if (this.config.cropCycles && this.config.cropCycles.length === 1) {
        intervalStartDate = this.config.cropCycles[0].startDate;
        intervalEndDate = this.config.cropCycles[0].endDate;
      } else {
        // todo: update this when porting this period selector to the compare crop cycle page
      }
      let startDate;
      let endDate;
      ({ startDate, endDate } = PeriodHelper.getCropCycleFilterCalendarDates(
        moment(intervalStartDate).startOf('day').toDate(),
        moment(intervalEndDate).endOf('day').toDate(),
        cropCycleFilter.periodInterval,
        cropCycleFilter.intervalLength));
      this.startDateControl.setValue(startDate);
      this.endDateControl.setValue(endDate);
    }
  }

  setCropCycleFilterCustomDatesInSessionStorage(cropCycleFilter) {
    const selectedItem = this.visiblePeriodSelectorItems.find(psi => psi.interval === cropCycleFilter.periodInterval);
    if (selectedItem) {
      let intervalStartDate;
      let intervalEndDate;
      if (this.config.cropCycles && this.config.cropCycles.length === 1) {
        intervalStartDate = this.config.cropCycles[0].startDate;
        intervalEndDate = this.config.cropCycles[0].endDate;
      } else {
        // todo: update this when porting this period selector to the compare crop cycle page
      }
      let startDate;
      let endDate;
      ({ startDate, endDate } = PeriodHelper.getCropCycleFilterCalendarDates(
        moment(intervalStartDate).startOf('day').toDate(),
        moment(intervalEndDate).endOf('day').toDate(),
        cropCycleFilter.periodInterval,
        cropCycleFilter.intervalLength));
        localStorage.setItem(this.lastPeriodSelectorStartDateKey, startDate.toString());
        localStorage.setItem(this.lastPeriodSelectorEndDayKey, endDate.toString());
    }
  }

  onPeriodSelectorButtonClick(selectedPeriodInterval: number) {
    this.setSelectedItem(selectedPeriodInterval);
  }

  saveSelectedCustomPeriod() {
    this.applyPredefinedPeriodSelection(this.startDateControl.value, this.endDateControl.value, NewPeriodSelectorInterval.Custom);
    this.popupVisible = false;
  }

  hideCustomPeriodSelector(event) {
    event.preventDefault();
    this.popupVisible = false;
  }

  onCustomPeriodSelectorHidding() {
    const previouslySelectedPeriodFilter = this.selectedPeriodFilter;
    const previouslySelectedPeriodInterval = previouslySelectedPeriodFilter.periodInterval;

    if (previouslySelectedPeriodInterval !== NewPeriodSelectorInterval.Custom) {
      this.visiblePeriodSelectorItems.find(p => p.interval === previouslySelectedPeriodInterval).isActive = true;
      this.canNavigate = this.visiblePeriodSelectorItems.find(psi => psi.isActive)?.hasNavigation;
      this.isCustomPeriodSelected = false;
    } else {
      this.isCustomPeriodSelected = true;
      this.canNavigate = true;
    }
  }

  onBackwardButtonClicked() {
    let previousFilter;
    if (!this.isCustomPeriodSelected) {
      const selectedItem = this.visiblePeriodSelectorItems.find(psi => psi.isActive);
      previousFilter = selectedItem.getPreviousPeriodFilter(this.selectedPeriodFilter);
      const calendarPreviousFilter = PeriodHelper.asCalendarFilter(previousFilter);
      if (calendarPreviousFilter) {
        previousFilter.formattedPeriod = this.getFormattedCalendarPeriodFilter(previousFilter);
      } else {
        previousFilter.formattedPeriod = this.getFormattedCropCyclePeriodFilter(previousFilter);
      }
    } else {
      const customCurrentFilter = PeriodHelper.asCalendarFilter(this.selectedPeriodFilter);
      previousFilter = PeriodHelper.getPreviousCustomPeriodDates(customCurrentFilter);
      previousFilter.formattedPeriod = this.getFormattedCalendarPeriodFilter(previousFilter);
    }
    this.checkIfGoingForwardIsPossible(previousFilter);
    this.checkIfGoingBackwardIsPossible(previousFilter);
    this.formattedDate = previousFilter.formattedPeriod;
    this.setPeriodFilter(previousFilter);
  }

  onForwardButtonClicked() {
    let nextFilter;
    if (!this.isCustomPeriodSelected) {
      const selectedItem = this.visiblePeriodSelectorItems.find(psi => psi.isActive);
      nextFilter = selectedItem.getNextPeriodFilter(this.selectedPeriodFilter);
      const calendarNextFilter = PeriodHelper.asCalendarFilter(nextFilter);
      if (calendarNextFilter) {
        nextFilter.formattedPeriod = this.getFormattedCalendarPeriodFilter(nextFilter);
      } else {
        nextFilter.formattedPeriod = this.getFormattedCropCyclePeriodFilter(nextFilter);
      }
    } else {
      const customCurrentFilter = PeriodHelper.asCalendarFilter(this.selectedPeriodFilter);
      nextFilter = PeriodHelper.getNextCustomPeriodDates(customCurrentFilter);
      nextFilter.formattedPeriod = this.getFormattedCalendarPeriodFilter(nextFilter);
    }
    this.formattedDate = nextFilter.formattedPeriod;
    this.checkIfGoingForwardIsPossible(nextFilter);
    this.checkIfGoingBackwardIsPossible(nextFilter);
    this.setPeriodFilter(nextFilter);
  }

  checkIfGoingForwardIsPossible(periodFilter: NewPeriodFilter) {
    const calendarFilter = PeriodHelper.asCalendarFilter(periodFilter);
    if (calendarFilter) {
      this.canGoForward = moment(calendarFilter.endDate).startOf('day') < moment().startOf('day');
    } else {
      const cropCycleFilter = PeriodHelper.asCropCycleFilter(periodFilter);
      this.canGoForward = cropCycleFilter.intervalLength < this.cropCycleItemsMaxIntervalLength;
    }
  }

  checkIfGoingBackwardIsPossible(periodFilter: NewPeriodFilter) {
    const cropCycleFilter = PeriodHelper.asCropCycleFilter(periodFilter);;
    if (cropCycleFilter) {
      this.canGoBackward = cropCycleFilter.intervalLength > 1;
    } else {
      this.canGoBackward = true;
    }
  }

  applyPredefinedPeriodSelection(newStartDate: Date, newEndDate: Date, interval: NewPeriodSelectorInterval) {
    const periodFilter = new NewCalendarPeriodFilter();
    periodFilter.startDate = moment(newStartDate).startOf('day').toDate();
    periodFilter.endDate = moment(newEndDate).endOf('day').toDate();
    periodFilter.periodInterval = interval;

    this.isCustomPeriodSelected = interval === NewPeriodSelectorInterval.Custom;
    periodFilter.formattedPeriod = this.getFormattedCalendarPeriodFilter(periodFilter);
    this.formattedDate = periodFilter.formattedPeriod;
    this.setPeriodFilter(periodFilter);
    this.checkIfGoingForwardIsPossible(periodFilter);
    this.checkIfGoingBackwardIsPossible(periodFilter);
  }

  setPeriodFilter(currentFilter: NewPeriodFilter) {
    let previousFilter;
    if (!this.isCustomPeriodSelected) {
      const selectedItem = this.visiblePeriodSelectorItems.find(psi => psi.isActive);
      if (selectedItem.hasNavigation) {
        previousFilter = selectedItem.getPreviousPeriodFilter(currentFilter);
      }
    } else {
      const customCurrentFilter = PeriodHelper.asCalendarFilter(currentFilter);;
      previousFilter = PeriodHelper.getPreviousCustomPeriodDates(customCurrentFilter);
    }
    this.selectedPeriodFilter = currentFilter;
    this.updateStorage(this.selectedPeriodFilter);
    this.appSettingsService.resetPeriodSelector();
    this.newPeriodSelectorService.setPeriodFilter({ currentPeriod: currentFilter, previousPeriod: previousFilter });
  }

  updateStorage(selectedPeriodFilter: NewPeriodFilter) {
    localStorage.setItem(this.lastPeriodSelectorIntervalKey, selectedPeriodFilter.periodInterval.toString());
    const calendarFilter = PeriodHelper.asCalendarFilter(selectedPeriodFilter);
    if (calendarFilter) {
      localStorage.setItem(this.lastPeriodSelectorStartDateKey, calendarFilter.startDate.toString());
      localStorage.setItem(this.lastPeriodSelectorEndDayKey, calendarFilter.endDate.toString());
    } else {
      const cropCycleFilter = PeriodHelper.asCropCycleFilter(selectedPeriodFilter);
      if (cropCycleFilter && cropCycleFilter.intervalLength) {
        localStorage.setItem(this.lastPeriodSelectorIntervalLenghtKey, cropCycleFilter.intervalLength.toString());
      }
      this.setCropCycleFilterCustomDatesInSessionStorage(cropCycleFilter);
    }
  }

  getFormattedCalendarPeriodFilter(periodFilter: NewCalendarPeriodFilter): string {
    let formatedPeriod = '';
    const selectedPeriodFilter = cloneDeep(periodFilter);
    selectedPeriodFilter.startDate.setHours(0, 0, 0, 0);
    selectedPeriodFilter.startDate.setDate(
      selectedPeriodFilter.startDate.getDate()
    );
    switch (periodFilter.periodInterval) {
      case NewPeriodSelectorInterval.Day.valueOf(): {
        const dateFormat = this.formattingService.getFormattedDate(selectedPeriodFilter.startDate);
        formatedPeriod = this.translateService.instant(
          'Shell.dates.formattedDay',
          {
            date: dateFormat,
          }
        );
        break;
      }
      case NewPeriodSelectorInterval.Week.valueOf(): {
        formatedPeriod = this.formattingService.getWeekFormatedPeriod(
          selectedPeriodFilter.startDate
        );
        break;
      }
      case NewPeriodSelectorInterval.Month.valueOf(): {
        const year = this.datePipe.transform(
          selectedPeriodFilter.endDate,
          'yyyy'
        );
        const month = this.datePipe.transform(
          selectedPeriodFilter.endDate,
          'MMMM'
        );
        formatedPeriod = this.translateService.instant(
          'Shell.dates.formattedMonth',
          {
            monthName: this.translateService.instant(`Shell.dates.Month.${month}`),
            yearNumber: year,
          }
        );
        break;
      }
      case NewPeriodSelectorInterval.Quarter.valueOf(): {
        const m = Math.floor(selectedPeriodFilter.startDate.getMonth() / 3) + 1;
        const quarter = m > 4 ? m - 4 : m;
        const year = this.datePipe.transform(
          selectedPeriodFilter.startDate,
          'yyyy'
        );
        formatedPeriod = this.translateService.instant(
          'Shell.dates.formattedQuarter',
          {
            quarterNumber: quarter,
            yearNumber: year,
          }
        );
        break;
      }
      case NewPeriodSelectorInterval.Year.valueOf(): {
        formatedPeriod = this.datePipe.transform(
          selectedPeriodFilter.endDate,
          'yyyy'
        );
        break;
      }
      case NewPeriodSelectorInterval.Custom.valueOf(): {
        const startDateFormated = this.formattingService.getFormattedDate(selectedPeriodFilter.startDate);
        const endDateFormated = this.formattingService.getFormattedDate(selectedPeriodFilter.endDate);
        formatedPeriod = this.translateService.instant(
          'Shell.dates.formattedCustomDate',
          {
            startDate: startDateFormated,
            endDate: endDateFormated,
          }
        );
        break;
      }
      case NewPeriodSelectorInterval.FourWeek.valueOf(): {
        formatedPeriod = this.formattingService.getFourWeekFormatedPeriod(
          selectedPeriodFilter.startDate
        );
        break;
      }
    }

    return formatedPeriod;
  }

  getFormattedCropCyclePeriodFilter(periodFilter: NewCropCyclePeriodFilter): string {
    let formatedPeriod = '';
    switch (periodFilter.periodInterval) {
      case NewPeriodSelectorInterval.CropWeek.valueOf(): {
        formatedPeriod = this.translateService.instant(
          'Shell.dates.formattedCropWeek',
          {
            weekNumber: periodFilter.intervalLength,
          }
        );
        break;
      }
      case NewPeriodSelectorInterval.ToDate.valueOf(): {
        formatedPeriod = this.translateService.instant(
          periodFilter.intervalLength === 1 ? 'Shell.dates.formattedToDateCropCycleWeek' : 'Shell.dates.formattedToDateCropCycleWeeks',
          {
            weekNumber: periodFilter.intervalLength,
          }
        );
        break;
      }
      case NewPeriodSelectorInterval.Full.valueOf(): {
        formatedPeriod = this.translateService.instant(
          'Shell.dates.formattedFullCropCycle'
        );
        break;
      }
      case NewPeriodSelectorInterval.CropToDate.valueOf(): {
        const cropCycle = this.config.cropCycles?.[0];
        if (cropCycle) {
          formatedPeriod = (moment(cropCycle.endDate).endOf('day') > moment().endOf('day'))
            ? this.translateService.instant('Shell.dates.formattedCropToDateActiveCropCycle', {
              startDate: this.formattingService.getFormattedDate(cropCycle.startDate)
            })
            : this.translateService.instant('Shell.dates.formattedCropToDateFinishedCropCycle', {
              startDate: this.formattingService.getFormattedDate(cropCycle.startDate),
              endDate: this.formattingService.getFormattedDate(cropCycle.endDate)
            });
        }
        break;
      }
    }
    return formatedPeriod;
  }
}
